import { Navigate, Outlet, createBrowserRouter, useNavigation } from 'react-router-dom';
import type { LoaderFunctionArgs } from 'react-router-dom';
import { Shell } from './layouts/Shell';
import { RouteError } from './layouts/RouteError';
import { conversationsLoader } from './layouts/Sidebar';
import { setSessionLostHandler } from './services/api';
import {
  clearPrincipalCache,
  requireAdmin,
  requireAuth,
  requireSuperadmin,
} from './services/session';
import { toastError } from './services/toast';

function ShellLayout() {
  const navigation = useNavigation();
  return (
    <Shell>
      {/* Перехід уже триває — притіняємо вміст, боковина лишається живою. */}
      <div
        style={
          navigation.state === 'loading' ? { opacity: 0.55, pointerEvents: 'none' } : undefined
        }
        aria-busy={navigation.state === 'loading' || undefined}
      >
        <Outlet />
      </div>
    </Shell>
  );
}

/** Principal для всіх дочірніх маршрутів — один запит на застосунок. */
async function shellLoader({ request }: LoaderFunctionArgs) {
  return { principal: await requireAuth(request) };
}

/** Доступ перевіряє лоадер, а не сторінка: закритий екран не блимає вмістом. */
export const router = createBrowserRouter([
  // Поза оболонкою: `requireAuth` перекидав би людину без сесії звідси на вхід по колу.
  {
    path: '/login',
    lazy: async () => {
      const m = await import('./pages/LoginPage/LoginPage');
      return { Component: m.LoginPage };
    },
  },
  {
    path: '/set-password',
    lazy: async () => {
      const m = await import('./pages/SetPasswordPage/SetPasswordPage');
      return { Component: m.SetPasswordPage };
    },
  },
  {
    id: 'shell',
    element: <ShellLayout />,
    errorElement: <RouteError />,
    loader: shellLoader,
    children: [
      // Імпорти вказують на файл, не на барель: за барелем Vite назве всі чанки «index-*.js».
      // Корінь — новий діалог, тому й лоадера не має: розмову створює сервер на перше питання.
      {
        index: true,
        lazy: async () => {
          const m = await import('./pages/ChatPage/ChatPage');
          return { Component: m.ChatPage };
        },
      },
      // Власний errorElement: після 404 на чужу розмову людина мусить лишитись у панелі.
      {
        path: 'c/:id',
        errorElement: <RouteError />,
        lazy: async () => {
          const m = await import('./pages/ChatPage/ChatPage');
          return { Component: m.ConversationChatPage, loader: m.conversationLoader };
        },
      },
      // Лоадер без Component: боковина читає його своїм fetcher, без глобальної ревалідації.
      {
        path: 'conversations',
        loader: conversationsLoader,
      },
      // «knowledge», а не «documents»: /documents — реальний контролер API, він забрав би адресу.
      // Роль вимагаємо до даних, інакше не-адмін побачив би 403 замість перекидання на головну.
      {
        path: 'knowledge',
        lazy: async () => {
          const m = await import('./pages/DocumentsPage/DocumentsPage');
          return {
            Component: m.DocumentsPage,
            loader: async (args: LoaderFunctionArgs) => {
              await requireAdmin(args.request);
              return m.documentsLoader(args);
            },
          };
        },
      },
      {
        path: 'unanswered',
        lazy: async () => {
          const m = await import('./pages/UnansweredPage/UnansweredPage');
          return {
            Component: m.UnansweredPage,
            loader: async (args: LoaderFunctionArgs) => {
              await requireAdmin(args.request);
              return m.unansweredLoader(args);
            },
          };
        },
      },
      // Окрема сторінка, не модалка: тред — самостійний екран зі своїм посиланням.
      // Власний errorElement: чуже/неіснуюче питання не має вибивати з панелі цілком.
      {
        path: 'unanswered/:id',
        errorElement: <RouteError />,
        lazy: async () => {
          const m = await import('./pages/UnansweredPage/UnansweredThreadPage');
          return {
            Component: m.UnansweredThreadPage,
            loader: async (args: LoaderFunctionArgs) => {
              await requireAdmin(args.request);
              return m.threadLoader(args);
            },
          };
        },
      },
      // Вимикач бота: ADMIN, а не SUPERADMIN — це операційна дія, не видача доступів.
      {
        path: 'bot',
        lazy: async () => {
          const m = await import('./pages/BotStatePage/BotStatePage');
          return {
            Component: m.BotStatePage,
            loader: async (args: LoaderFunctionArgs) => {
              await requireAdmin(args.request);
              return m.botStateLoader();
            },
          };
        },
      },
      {
        path: 'users',
        lazy: async () => {
          const m = await import('./pages/UsersPage/UsersPage');
          return {
            Component: m.UsersPage,
            loader: async (args: LoaderFunctionArgs) => {
              await requireSuperadmin(args.request);
              return m.usersLoader();
            },
          };
        },
      },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

/** Через `router.navigate`, а не перезавантаження: набраний у чаті текст мусить вижити. */
setSessionLostHandler(() => {
  clearPrincipalCache();
  if (!window.location.pathname.startsWith('/login')) {
    toastError('Термін дії входу вийшов', 'Увійдіть знову — ми повернемо вас на цей екран.');
    void router.navigate(`/login?from=${encodeURIComponent(window.location.pathname)}`, {
      replace: true,
    });
  }
});
